import React, {useEffect, useRef, useState} from 'react';
import {useEditor} from '@craftjs/core';
import {classNames} from 'utils';
import {
  Transitioner,
  EditorToolboxContainer,
  EditorToolboxImage,
  EditorToolboxParagraph,
  Button,
  EditorLayers,
  EditorToolboxQR,
  EditorToolboxLauncher,
} from 'components';

import useProjectStore from '../../stores/useProjectStore';

interface ISidebarLeftEditorProps {}

const SidebarLeftEditor = ({}: ISidebarLeftEditorProps) => {
  const [tab, setTab] = useState<'layers' | 'components'>('layers');
  const updateEditor = useProjectStore(s => s.updateEditor);

  const prevIds = useRef<string[]>([]);
  const {store, actions, query, nodes} = useEditor(state => ({
    nodes: state.nodes,
  }));

  useEffect(() => {
    updateEditor(query.serialize());
  }, [updateEditor, query, nodes]);

  useEffect(() => {
    const unsubscribe = store.subscribe(
      state => Object.keys(state.nodes),
      (currentIds: string[]) => {
        const addedId = currentIds.find(id => !prevIds.current.includes(id));
        if (addedId) {
          setTab('layers');
          actions.selectNode(addedId);
          // Recursively expand parent layers to show added layer
          const parentIds = query.node(addedId).ancestors();
          if (parentIds.length > 0) {
            parentIds.reverse();
            (async () => {
              for (const parentId of parentIds) {
                await new Promise(resolve => setTimeout(resolve, 50));
                const selector = `div.craft-layer-node.${parentId} button.expand-toggle`;
                const parentElement = document.querySelector(selector) as HTMLElement | null;
                if (parentElement?.classList?.contains('rotate-0')) {
                  parentElement.click();
                }
              }
            })();
          }
        }
        prevIds.current = currentIds;
      },
    );
    return () => {
      unsubscribe();
    };
  }, [store, actions, query, setTab]);

  return (
    <Transitioner>
      <div className="relative flex flex-col mb-6">
        <div className="grid grid-cols-2 items-center gap-2 mb-4">
          <Button
            text="Layers"
            size="extra-small"
            variant={tab === 'layers' ? 'primary' : 'secondary'}
            onClick={() => {
              setTab('layers');
            }}
          />
          <Button
            text="Components"
            size="extra-small"
            variant={tab === 'components' ? 'primary' : 'secondary'}
            onClick={() => {
              setTab('components');
            }}
          />
        </div>
        <div className={classNames(tab !== 'layers' && 'hidden')}>
          <EditorLayers />
        </div>
        <div className={classNames(tab !== 'components' && 'hidden', 'grid grid-cols-2 gap-3')}>
          <EditorToolboxContainer />
          <EditorToolboxParagraph />
          <EditorToolboxImage />
          <EditorToolboxQR />
          <EditorToolboxLauncher />
        </div>
      </div>
    </Transitioner>
  );
};

export default React.memo(SidebarLeftEditor);
